import { Component, OnInit, EventEmitter, Output } from '@angular/core'
import { Validators, FormBuilder, FormControl } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import { BdcContentV2Service, bdcLanguages } from '@tokeet-frontend/channel-content'
import {
  Account,
  ActionFailed,
  compactObject,
  Destroyable,
  integer,
  selectCurrentAccount,
  untilDestroy,
  User,
  userFullname,
  validateForm,
} from '@tokeet-frontend/tv3-platform'
import { distinctUntilChanged, finalize } from 'rxjs/operators'
import * as lodash from 'lodash'

@Component({
  selector: 'app-bdc-create-property-entity-id',
  templateUrl: './bdc-create-property-entity-id.component.html',
  styleUrls: ['./bdc-create-property-entity-id.component.scss'],
})
export class BdcCreatePropertyEntityIdComponent extends Destroyable implements OnInit {
  @Output() next = new EventEmitter<number>()

  legalEntityIdCtrl = new FormControl(null, [Validators.required])
  userCtrl = new FormControl(null, [Validators.required])

  account: Account
  languages = bdcLanguages

  form = this.fb.group({
    legal_contact_name: [null, [Validators.required]],
    legal_contact_email: [null, [Validators.required]],
    legal_contact_phone_number: [],
    legal_name: [],
    company_name: [null, [Validators.required]],
    preferred_language: [undefined, [Validators.required]],
    website: [],
    total_number_of_properties: [null, [Validators.min(1), integer()]],
    country: [],
    city: [],
    zipcode: [],
    street: [],
    contract_countries: [[]],
  })

  isLoading = false

  constructor(private fb: FormBuilder, private store: Store<any>, private contentService: BdcContentV2Service) {
    super()
  }

  ngOnInit(): void {
    this.legalEntityIdCtrl.valueChanges.pipe(distinctUntilChanged(), untilDestroy(this)).subscribe((value) => {
      if (value) {
        this.userCtrl.reset(null)
        this.userCtrl.disable()
      } else {
        this.userCtrl.enable()
      }
    })
    this.userCtrl.valueChanges.pipe(distinctUntilChanged(), untilDestroy(this)).subscribe((userId) => {
      if (userId) {
        this.legalEntityIdCtrl.reset(null)
        this.legalEntityIdCtrl.disable()
      } else {
        this.legalEntityIdCtrl.enable()
      }
    })

    this.store.pipe(select(selectCurrentAccount), untilDestroy(this)).subscribe((account) => {
      this.account = account
    })
  }

  onUserSelect([user]: User[]) {
    const address = user.address
    const userLanguage = user.attributes?.language || 'en'
    this.form.patchValue({
      legal_contact_name: userFullname(user),
      legal_contact_email: user.primaryEmail,
      legal_contact_phone_number: (user.phone || '').replace(/\s/g, ''),
      legal_name: this.account.name,
      company_name: this.account.name,
      city: address.city,
      country: address.countryCode,
      zipcode: address.postalCode || address.zip,
      street: address.address,
      contract_countries: address.countryCode ? [address.countryCode] : [],
      website: this.account.website,
      preferred_language: lodash.find(bdcLanguages, (l) => lodash.startsWith(l.code, userLanguage))?.code,
    })
  }

  onSave() {
    if (this.legalEntityIdCtrl.value) {
      this.isLoading = true
      this.contentService
        .getLegalEntity(this.legalEntityIdCtrl.value)
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe(
          (res) => {
            this.next.emit(res.legal_entity_id)
          },
          (error) => {
            this.store.dispatch(ActionFailed({ error }))
          }
        )
    } else if (this.userCtrl.value) {
      if (!validateForm(this.form) || this.form.invalid) {
        return
      }
      this.isLoading = true
      this.contentService
        .createLegalEntity(compactObject(this.form.getRawValue()))
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe(
          (res) => {
            this.legalEntityIdCtrl.setValue(res.legal_entity_id)
            this.next.emit(res.legal_entity_id)
          },
          (error) => {
            this.store.dispatch(ActionFailed({ error }))
          }
        )
    }
  }
}
